import { useId, useState, type FormEvent } from "react";
import { useEnvironments, usePromoteService } from "../../api/queries";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface PromoteServiceModalProps {
  open: boolean;
  projectId: string;
  serviceId: string;
  serviceName: string;
  onClose: () => void;
}

/**
 * Promotes a service into a target environment (ADR-0035 §2/§3). Gated to the
 * roles that hold environment:promote (admin + operator); the caller renders it
 * only for those, and the server enforces the real decision on every request
 * (S-8).
 *
 * The eligible TARGETS offered are the environments that accept promotions —
 * those with a declared promotion source. The full source-chain check (is THIS
 * service's current environment actually this target's source?) is left to the
 * server: computing it client-side would need per-node/per-placement
 * environment data the node contract doesn't yet surface, so this offers the
 * plausible targets and surfaces the server's own refusal clearly if the chain
 * doesn't line up (Q-7).
 */
export function PromoteServiceModal({
  open,
  projectId,
  serviceId,
  serviceName,
  onClose,
}: PromoteServiceModalProps) {
  const targetId = useId();
  const { pushToast } = useToast();
  const { data, isPending, isError } = useEnvironments(undefined, { enabled: open });
  const mutation = usePromoteService();
  const [selectedEnvId, setSelectedEnvId] = useState("");

  const eligibleTargets = (data?.items ?? []).filter((env) => Boolean(env.sourceEnvId));

  function handleClose() {
    setSelectedEnvId("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedEnvId) return;
    mutation.mutate(
      { envId: selectedEnvId, body: { projectId, serviceId } },
      {
        onSuccess: () => {
          pushToast({ title: "Promotion started.", tone: "success" });
          handleClose();
        },
        // Inline form-error below carries the server's own refusal (e.g. no
        // promotion source, or the chain doesn't point here) — no toast so it
        // stays legible while the operator picks a different target.
      },
    );
  }

  const canSubmit = Boolean(selectedEnvId) && !mutation.isPending;

  return (
    <Modal open={open} title={`Promote "${serviceName}"`} onClose={handleClose} initialFocusId={targetId}>
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't promote this service."}
          </p>
        )}

        <p className="form-field__hint">
          Promotion re-deploys the bytes running in the target's source environment onto the target.
          The source is fixed by the target's promotion chain.
        </p>

        {isError ? (
          <p className="form-error" role="alert">
            Couldn't load environments. Close and try again in a moment.
          </p>
        ) : isPending ? (
          <p className="form-field__hint">Loading environments…</p>
        ) : eligibleTargets.length === 0 ? (
          <p className="form-field__hint">
            No environment has a promotion source configured yet, so there's nowhere to promote to.
          </p>
        ) : (
          <label className="form-field" htmlFor={targetId}>
            <span>Target environment</span>
            <select id={targetId} value={selectedEnvId} onChange={(e) => setSelectedEnvId(e.target.value)} required>
              <option value="" disabled>
                Choose a target…
              </option>
              {eligibleTargets.map((env) => (
                <option key={env.id} value={env.id}>
                  {env.name} ({env.class})
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Promoting…" : "Promote"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
